/* @flow */

import dynamic from 'next/dynamic';
import getConfig from 'next/config';
import { withRouter } from 'next/router';

import * as React from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import debounce from 'lodash.debounce';
import omit from 'lodash.omit';

import SortDirection from 'react-virtualized/dist/commonjs/Table/SortDirection';
import SortIndicator from 'react-virtualized/dist/commonjs/Table/SortIndicator';

import withI18n from '../utils/withI18n';
import { getLabel } from '../utils/i18n';
import shallowEqual from '../utils/shallowEqual';
import {
  decRound, addPosNeg, formatNumber, decimalPlaces,
} from '../utils/number';
import zIndexScale from '../utils/zIndexScale';
import { setItem } from '../utils/localStorage';
import type {
  RowGetter, CellDataGetter, CellRenderer, HeaderRenderer, OnRowClick,
} from '../utils/dataTypes';

import FlexBox from './FlexBox';
import Dropdown from './Dropdown';
import SearchInput from './SearchInput';
import EmptyResult from './EmptyResult';
import ScrollableTable from './ScrollableTable';
import SymbolsRowRenderer from './SymbolsList/SymbolsRowRenderer';
import FiatPrice from './FiatPrice';
import MarginIcon from './Margin/MarginIcon';
import Icon from './Icon';
import { setInitFavorite as setInitFavoriteAction } from '../actions/actionsSet/assetService';
import {
  setSelectedPair as setSelectedPairAction,
  setSelectedAssetTab as setSelectedAssetTabAction,
  setSelectedStableCoinTabs as setSelectedStableCoinTabsAction,
  setIsMarginPair as setIsMarginPairAction,
} from '../actions/settings';

const PRICE_COL_STYLE = { overflow: 'visible' };
const PAIR_COL_STYLE = { justifyContent: 'space-between', paddingRight: '6px' };
const DEFAULT_WIDTH = 270; // ssr width
const DEFAULT_HEIGHT = 340;
const STABLE_COIN_BASES = ['ALTS', 'USDⓈ'];

const FavoriteStarPlaceholder = () => <i className="iconfont icon-xingxing" />;

const FavoriteStar = dynamic(import('./SymbolsList/FavoriteStar'), {
  ssr: false,
  loading: FavoriteStarPlaceholder,
});

const {
  publicRuntimeConfig: {
    routePrefix: ROUTE_PREFIX,
    enableMargin,
  },
} = getConfig();

const prefixLink = uri => ROUTE_PREFIX + uri;


const ROW_STYLE = { cursor: 'pointer' };
const ALIGN_RIGHT = { 'justifyContent': 'flex-end' };
const HEADER_HEIGHT = 20;
const ROW_HEIGHT = 20;

const Tabs = styled(FlexBox)`
  background-color: ${({ theme }) => theme.boxheadbg};
  flex-wrap: nowrap;
  height: 30px;
  padding: 4px;
`;

const LeftPanel = styled(FlexBox)`
  background: none;
  .iconfont {
    font-size: 14px;
  }
`;

const RightPanel = styled(LeftPanel)`
  flex-wrap: nowrap;
  margin-left: auto;
  align-items: center;
  position: relative;
`;

const RadiosPanel = styled(RightPanel)`
  margin-left: 8px;
`;

export const Tab = styled.button`
  border: 1px solid ${({ theme, selected }) => selected ? theme.activeBorder : theme.border};
  background-color: ${({ theme }) => theme.tabBg};
  color: ${({ theme, selected }) => selected ? theme.buttonColorActive || '' : ''};
  cursor: pointer;
  padding: 4px 5px;
  margin: ${({ margin }) => margin || '0 2px'};
  outline: 0;
  line-height: 100%;
  font-size: 12px;
`;

const TabColor = styled(Tab)`
  background-color: ${({ theme, selected }) => selected ? theme.activeBg : theme.bg};

  &.stable-currency-collection {
    min-width: 4.1em;
  }
  &.stable-currency-cta {
    border-left: none;
    padding: 4px 3px;
    position: relative;
  }
`;

const MenuIcon = styled.span`
  display: inline-block;
  ${({ toggled }) => toggled ? 'transform: rotate(180deg); transition: transform .3s;' : ''}
`;

const FavoritesTab = styled(Tab)`
  display: flex;
  align-items: center;
  position: relative;
  margin-right: 2px;

  & > i {
    color: inherit !important;
  }
`;

const MarginTab = styled(FavoritesTab)``;

const Tooltip = styled.div`
    padding: 4px 6px;
    border-radius:3px;
    background: ${({ theme }) => theme.FormRow.FiatPriceBoxBg};
    color: ${({ theme }) => theme.FormRow.FiatPriceBoxColor};
    position: absolute;
    left: 50%;
    bottom: 28px ;
    transform: translateX(-50%);
    white-space: nowrap;
    z-index: ${zIndexScale.modal};
    & :after {
        content: " ";
        border: 6px solid transparent;
        border-top-color: ${({ theme }) => theme.FormRow.FiatPriceBoxBg};
        position: absolute;
        bottom: -11px;
        left: 50%;
        margin-left: -5px;
      }
  `;

const Form = styled.form`
  width: 100%;
`;

const RadioPanelLabel = styled.label`
  cursor: pointer;
  margin-right: .5em;
  user-select: none;
  white-space: nowrap;
  &:hover i {
    box-shadow: inset 0 0 0 3px ${({ theme }) => theme.bg};
    background: #888;
  }
`;

const RadioPanelInput = styled.input`
  visibility: hidden;
  position: absolute;
  &:checked + i {
    box-shadow: inset 0 0 0 3px ${({ theme }) => theme.bg};
    background: ${({ theme }) => theme.highlight};
  }
`;

const RadioPanelIcon = styled.i`
  display: inline-block;
  vertical-align: middle;
  width: 13px;
  height: 13px;
  border-radius: 50%;
  box-shadow: inset 0 0 0 8px ${({ theme }) => theme.bg};
  border: 1px solid ${({ theme }) => theme.tabBorderColor};
  background: #999;
  margin-right: 4px;
`;

const Amount = styled.span`
  display: flex;
  flex-direction: column;
  font-size: 100%;
  text-align: right;
`;
const GreenAmount = styled(Amount)` align-items: flex-end; font-size: 100%; color: ${({ theme }) => theme.up};  `;
const RedAmount = styled(Amount)` align-items: flex-end; font-size: 100%; color: ${({ theme }) => theme.down}; `;

const TableFlexBox = styled(FlexBox)`
  position:relative;
  .ReactVirtualized__Table__headerRow {
    border-bottom: 1px solid ${({ theme }) => theme.border};
    padding: 4px 0;
  }
  .ReactVirtualized__Table__row:nth-child(even) {
    background: ${({ theme }) => theme.tableRowBg};
  }
  .ReactVirtualized__Table__row.active {
    background: ${({ theme }) => theme.tableRowHoverBg};
    font-weight: bold;
  }
  .ReactVirtualized__Table__rowColumn {
    margin-left: 0;
    margin-right: 0;
    button {
      padding-left: 6px;
      padding-right: 6px;
    }
  }
  
`;
const PaddedFlexBox = styled(FlexBox)` padding: 4px; `;

const HeaderCell = styled.div`
  text-align: ${({ dataKey }) => dataKey === 'change' || dataKey === 'volume' ? 'right;' : 'left;'};
  white-space: nowrap;
`;

const FiatPriceBox = styled.div`
  box-shadow: 0 0 5px ${({ theme }) => theme.miniSymbolsFiatPrice.shadowColor};
  padding: 6px 8px;
  color: ${({ theme }) => theme.miniSymbolsFiatPrice.color};
  font-size: 12px;
  margin: -8px 0 0 0px;
  transform: translate(-100%,0%);
  background: ${({ theme }) => theme.miniSymbolsFiatPrice.bg};
  border-radius: 3px;
  position:absolute;
  left: 0;
  top: ${({ index, rowHeight, scrollTop }) => `${(rowHeight * (index + 1)) - scrollTop}px`};
  z-index: ${zIndexScale.tooltip};
  & :before {
      content: ' ';
      display: inline-block;
      border: 6px solid transparent;
      border-left-color: ${({ theme }) => theme.miniSymbolsFiatPrice.bg};
      position: absolute;
      right: -11px;
      top: 50%;
      z-index: 9;
      margin-top: -6px;
    }
  > div {
      display: inline-block;
      vertical-align:text-bottom;
      margin: 0 4px 0 0;
    }
  & a:hover i {
      color: ${({ theme }) => theme.miniSymbolsFiatPrice.linkHoverColor};
    }
`;

const StableCurrencyListItem = styled.li`
  color: ${({ theme, active }) => active ? theme.chartButtonColorActive : theme.fontColor};
  cursor: pointer;
  display: flex;
  flex: 1 1 100%;
  width: 100%;
  box-shadow: rgba(0, 0, 0, 0.35);
  ${({ theme, active }) => active ? `border-top: 1px solid ${theme.border}` : ''}
  border-left: 1px solid ${({ theme }) => theme.border};
  border-right: 1px solid ${({ theme }) => theme.border};
  button {
    background-color: ${({ theme, active }) => active ? theme.chartbuttonbg : theme.bg};
    color:${({ theme, active }) => active ? theme.highlight : theme.fontColor};
    border: 0;
    cursor: pointer;
    font-weight: ${({ active }) => active ? '500' : ''};
    padding: .5em;
    text-align: left;
    width: 100%;
    flex: 0 0 100%;

    &:focus {
      outline: none;
    }

    &:hover {
      background-color: ${({ theme }) => theme.chartbuttonbg};
      color: ${({ theme }) => theme.highlight};
    }
  }

  &:last-child {
    border-bottom: 1px solid ${({ theme }) => theme.border};
  }
`;

const SubItem = styled.span`
  display: inline-block;
  height: 11px;
  text-align: center;
  width: 11px;
`;

const StableCurrencyContainer = styled(FlexBox)`
  position: relative;
`;

const StableCurrencyList = styled.ul`
  display: flex;
  flex-basis: 100px;
  flex-wrap: wrap;
  list-style: none;
  margin: 0;
  max-width: 5.5em;
  padding: 0;
  position: absolute;
  right: 0;
  top: 1.3em;
  z-index: ${zIndexScale.dropdown};
  white-space: nowrap;
  box-shadow: 1px 1px 10px rgba(0, 0, 0, 0.35);
`;

const PairText = styled.span`
  overflow: hidden !important;
  text-overflow: ellipsis !important;
`;

type StableCurrencyDropdownProps = {
  keys: Array<string>,
  hide: boolean,
  onMouseLeave: Function,
  onClickHandler: Function,
  selectedTab: string,
}

export const StableCurrencyDropdown = (props: StableCurrencyDropdownProps) => (
  <Dropdown
    component={StableCurrencyList}
    keys={props.keys}
    isOpen={!props.hide}
    onMouseLeave={props.onMouseLeave}
    {...props}
  >
    {({ key, index }) => (
      <StableCurrencyListItem key={key} active={key === props.selectedTab}>
        <button
          onClick={() => props.onClickHandler(key)}
          value={key}
          type="button"
        >
          {index === 0 ? <Icon type="hamburger" size={11} /> : <SubItem>-</SubItem>} {key}
        </button>
      </StableCurrencyListItem>
    )}
  </Dropdown>
);

const mapStateToProps = ({
  tickers, favorites, locale, selectedAssetTab, selectedStableCoinTabs, marginSymbolsFlat,
}) => ({
  tickers, favorites, locale, selectedAssetTab, selectedStableCoinTabs, marginSymbolsFlat,
});

const mapDispatchToProps = {
  setInitFavorite: setInitFavoriteAction,
  setSelectedPair: setSelectedPairAction,
  setSelectedAssetTab: setSelectedAssetTabAction,
  setSelectedStableCoinTabs: setSelectedStableCoinTabsAction,
  setIsMarginPair: setIsMarginPairAction,
};

type Ticker = {
  s: string,
  ba: string,
  qa: string,
  c: number,
  o: number,
  q: number,
  tickSize: number,
  pn: string,
}

type Tickers = { [string]: Ticker }

type Props = {
  tickers: Tickers,
  i18n: Object,
  locale: string,
  favorites: Array<Object>,
  symbol: string,
  setInitFavorite: Function,
  setSelectedPair: Function,
  setSelectedAssetTab: Function,
  setSelectedStableCoinTabs: Function,
  selectedAssetTab: string,
  selectedStableCoinTabs: Object,
  omitTabs: boolean,
  omitFavorites: boolean,
  keys: Object,
  marginSymbols: Object,
  marginSymbolsFlat: Array<Object>,
  router: Object,
  setIsMarginPair: Function,
}

type State = {
  filterByAsset: string,
  showFavorites?: boolean,
  showMarginTooltip: boolean,
  keyword: string,
  sortBy?: string,
  sortDirection?: string,
  togglePanel?: string,
  initFavorites?: boolean,
  priceColor: Object,
  lastPriceCollection: Object,
  isShowTip?: boolean,
  onHoverSymbol: Object,
  isScrolling?: boolean,
  scrollHeight?: number,
  stableCurrenciesMenuStatus?: Object,
}

export default
@withI18n
@withRouter
@connect(mapStateToProps, mapDispatchToProps)
class MiniSymbolsList extends React.Component<Props, State> {
  _table = React.createRef();

  tableScrollTop = 0;

  _tickers = [];

  _startIndex = 0;

  _stopIndex = 15;

  static defaultProps = {
    tickers: [],
    i18n: {},
    locale: 'en',
    favorites: [],
    symbol: '',
    router: {},
  }

  constructor(props: Props) {
    super(props);
    const { tickers } = props;
    const priceColor = {};
    const lastPriceCollection = {};
    const stableCurrenciesMenuStatus = {};
    Object.keys(tickers).map(item => {
      priceColor[item] = 0;
      lastPriceCollection[item] = [1];
      return null;
    });
    STABLE_COIN_BASES.forEach((el: string) => {
      stableCurrenciesMenuStatus[el] = false;
    });
    this.state = {
      filterByAsset: 'BTC',
      showFavorites: false,
      showMarginTooltip: false,
      keyword: '',
      sortBy: 'pair',
      sortDirection: SortDirection.ASC,
      togglePanel: 'change',
      initFavorites: false,
      priceColor,
      lastPriceCollection,
      isShowTip: false,
      onHoverSymbol: {},
      isScrolling: false,
      scrollHeight: 0,
      stableCurrenciesMenuStatus,
    };
  }

  componentDidMount() {
    const { router: { query: { type } } } = this.props;
    // init favorite
    this.props.setInitFavorite();
    // if margin tab
    if (type === 'margin') {
      this._handleMarginTabClick();
    }
  }

  static getDerivedStateFromProps({ tickers }: Props, { lastPriceCollection }: State) {
    const tickersArr = Object.entries(tickers);
    const priceColor = {};
    tickersArr.map((item: [string, Ticker | any]) => {
      const { s, c } = item[1];
      const prevPrice = lastPriceCollection[s];
      if (Number(c) > Number(prevPrice)) {
        priceColor[s] = 1;
      } else if (Number(c) === Number(prevPrice)) {
        priceColor[s] = 0;
      } else {
        priceColor[s] = -1;
      }
      lastPriceCollection[s] = c;
      return null;
    });
    return { priceColor, lastPriceCollection };
  }

  shouldComponentUpdate(nextProps: Props, nextState: State): boolean {
    const { priceColor } = this.state;
    const visibleKeys = this._tickers.slice(this._startIndex, this._stopIndex).map(t => t[0]);
    const visiblePriceColor = visibleKeys
      .reduce((obj, key) => {
        obj[key] = priceColor[key];
        return obj;
      }, {});
    const nextVisiblePriceColor = visibleKeys
      .reduce((obj, key) => {
        obj[key] = nextState.priceColor[key];
        return obj;
      }, {});
    //  shallow compare props except tickers ,shallow compare state, state.priceColor only compare visible part
    return !shallowEqual(omit(this.props, 'tickers'), omit(nextProps, 'tickers'))
      || !shallowEqual(omit(this.state, 'priceColor'), omit(nextState, 'priceColor'))
      || !shallowEqual(visiblePriceColor, nextVisiblePriceColor);
  }

  // getters should be very simple, renderers can be more complex.

  _cellGetter = ({ rowData }: CellDataGetter) => rowData[1]

  // begin scrolling performance fix (favorites star)

  _setIsScrollingNow = (isScrolling: boolean) => { this.setState({ isScrolling }); };

  /* eslint-disable-next-line */
  _setIsScrollingDelayed = debounce(this._setIsScrollingNow, 50);

  _rowGetter = ({ index }: RowGetter) => this._tickers[index];

  _rowRenderer = (args: Object) => {
    const { isScrolling } = args;
    if (isScrolling !== this.state.isScrolling) {
      isScrolling ?
        this._setIsScrollingNow(true) :
        this._setIsScrollingDelayed(false);
    }
    return <SymbolsRowRenderer {...args} scroll={false} />;
  }

  // end scrolling performance fix

  _noRowsRenderer = (props: Props) => {
    if (this.props.selectedAssetTab === 'favorites') return <EmptyResult {...props} message={getLabel('exchange-trade-noFavorites', this.props.i18n)} />;
    return null;
  }

  _getFavoritesColumn = (usePlaceholder: boolean) => (
    <ScrollableTable.Column
      label=""
      dataKey=""
      width={28}
      flexShrink={0}
      cellRenderer={usePlaceholder ?
        FavoriteStarPlaceholder :
        this._cellRendererFavorite}
      disableSort={true}
    />
  );

  _cellRendererFavorite = ({ rowData }: CellRenderer) => {
    const { favorites = [] } = this.props;
    const symbol = rowData[0];
    const active = favorites.includes(symbol);
    return <FavoriteStar key={symbol} symbol={rowData[0]} active={active} />;
  }

  _cellGetterPair = ({ rowData }: CellDataGetter) => rowData && rowData[0]

  _cellRendererPair = ({ rowData }: CellRenderer) => {
    const { marginSymbolsFlat } = this.props;
    const isMarginPair = this._isMarginPair(rowData[0], marginSymbolsFlat);
    const { ba, qa } = rowData[1];
    /* eslint-disable */
    if (isMarginPair) {
      return <React.Fragment>
        <PairText>{`${ba}/${qa}`} </PairText>
        <MarginIcon/>
      </React.Fragment>;
    }
    return `${ba}/${qa}`;
  }

  _cellGetterPrice = ({ rowData }: CellDataGetter) => [rowData[1].s, rowData[1].c, rowData[1].o]

  _cellRendererPrice = (cellData: CellRenderer, priceColor: Object) => {
    if (cellData) {
      const {
        c,
        s,
        tickSize,
      } = cellData;
      const tk = Math.abs(Math.log10(tickSize));
      const colorState = priceColor[s];
      const price = formatNumber(c, tk);
      if (colorState === 1) {
        return (
          <FlexBox>
            <GreenAmount>{price}</GreenAmount>
          </FlexBox>
        );
      } if (colorState === 0) {
        return (
          <FlexBox>
            <Amount>{price}</Amount>
          </FlexBox>
        );
      }
      return (
        <FlexBox>
          <RedAmount>{price}</RedAmount>
        </FlexBox>
      );
    }
    return null;
  }

  _cellGetterChange = ({ rowData }: CellDataGetter) => [rowData[1].c, rowData[1].o];

  _cellRendererChange = ({ cellData }: CellRenderer) => {
    if (!Number(cellData[1])) return <Amount>–</Amount>;
    return (+cellData[0] >= +cellData[1]) ?
      <GreenAmount>{this._calculateChange(cellData)}</GreenAmount> :
      <RedAmount>{this._calculateChange(cellData)}</RedAmount>;
  }

  _cellGetterVolume = ({ rowData }: CellDataGetter) => rowData && rowData[1];

  _cellRendererVolume = ({ cellData: { q } }: CellRenderer) => (
    <Amount>{formatNumber(q, 0)}</Amount>
  );

  _calculatePrice = ([symbol, price]: [string, number]) => {
    const { tickers } = this.props;
    const decimals = decimalPlaces(tickers[symbol].tickSize);
    return formatNumber(price, decimals);
  }

  _calculateChange = ([price, open]: [number, number]) => {
    if (parseFloat(price) === 0 && parseFloat(open) === 0) return '–';
    return `${addPosNeg(decRound(((parseFloat(price) - parseFloat(open)) / parseFloat(open)) * 100, 2), false)}%`;
  }

  _onSearchBarChange = (value: string) => {
    this.setState({ keyword: value });
  };

  _onTogglePanelChange = (ev: MouseEvent) => {
    if (!ev || !ev.target || (!(ev.target instanceof HTMLInputElement))) return;
    const { value } = ev.target;
    this.setState({ togglePanel: value });
  };

  _onRowClick = ({ index }: OnRowClick) => {
    const { marginSymbolsFlat } = this.props;
    const isMarginPair = this._isMarginPair(this._tickers[index][0], marginSymbolsFlat);
    const label = 'bn.selectedPair';
    setItem(window.localStorage, label, `${this._tickers[index][1].ba}_${this._tickers[index][1].qa}`);
    this.props.setSelectedPair(`${this._tickers[index][1].ba}_${this._tickers[index][1].qa}`);
    isMarginPair ? this.props.setIsMarginPair(true) : this.props.setIsMarginPair(false);
  }

  _onRowsRendered = (overscanStartIndex: Object) => {
    const { startIndex, stopIndex } = overscanStartIndex;
    this._startIndex = startIndex;
    this._stopIndex = stopIndex;
  }

  _getTickers = ({
    keyword, sortBy, sortDirection,
  }: State) => (Object.entries(this.props.tickers).filter(([s, { ba, pm, qa }]: [string, any]) => {
    try {
      const { selectedAssetTab, selectedStableCoinTabs,marginSymbolsFlat } = this.props;
      const stableAssetTab = selectedStableCoinTabs[selectedAssetTab];
      if (selectedAssetTab === 'favorites') {
        return this._getFavorites().includes(s) &&
          ba.toLowerCase().includes(keyword.toLowerCase());
      }
      if (selectedAssetTab === 'margin') {
        return marginSymbolsFlat.map(m=>m.symbol).includes(s)&& ba.toLowerCase().includes(keyword.toLowerCase());
      }
      if (ba !== pm && stableAssetTab) {
        if (stableAssetTab && pm.toLowerCase() === stableAssetTab.toLowerCase()) {
          // Don't need to match tab name here
          return ba.toLowerCase().includes(keyword.toLowerCase());
        }
        return s.endsWith(stableAssetTab) &&
          ba.toLowerCase().includes(keyword.toLowerCase());
      }
      return s.endsWith(selectedAssetTab) &&
        ba.toLowerCase().includes(keyword.toLowerCase());
    } catch (err) {
      // invalid regexp. ignore.
      const { selectedAssetTab } = this.props;
      return s.endsWith(selectedAssetTab);
    }
  }).sort((a: [string, Ticker | any], b: [string, Ticker | any]) => {
    if (sortBy === 'price') {
      return (sortDirection === 'ASC') ? (a[1].c - b[1].c) : (b[1].c - a[1].c);
    } if (sortBy === 'volume') {
      return (sortDirection === 'ASC') ? (a[1].q - b[1].q) : (b[1].q - a[1].q);
    } if (sortBy === 'pair') {
      const symbolA = a[0].toUpperCase();
      const symbolB = b[0].toUpperCase();
      if ((symbolA < symbolB && sortDirection === 'ASC') ||
        (symbolA > symbolB && sortDirection !== 'ASC')) return -1;
      if ((symbolA > symbolB && sortDirection === 'ASC') ||
        (symbolA < symbolB && sortDirection !== 'ASC')) return 1;
      return 0;
    } if (sortBy === 'change') {
      const aCh = parseFloat(this._calculateChange([a[1].c, a[1].o]));
      const bCh = parseFloat(this._calculateChange([b[1].c, b[1].o]));
      if (sortDirection === 'ASC') {
        if (!Number.isNaN(aCh) && !Number.isNaN(bCh)) {
          if(aCh===bCh) return 0
          return aCh > bCh ? 1 : -1;
        }
        if (Number.isNaN(bCh)) {
          return -1;
        }
        return aCh - bCh;
      }
      if (!Number.isNaN(aCh) && !Number.isNaN(bCh)) {
        if(aCh===bCh) return 0
        return aCh < bCh ? 1 : -1;
      }
      if (Number.isNaN(bCh)) {
        return -1;
      }
      return bCh - aCh;
    }
    return 0;
  }): Array<[string, any]>);

_handleFavoriteTabClick = () => {
  this.setState({ showFavorites: true });
  const assetTabLabel = 'bn.selectedAssetTab';
  setItem(window.localStorage, assetTabLabel, 'favorites');
  this.props.setSelectedAssetTab('favorites');
}

_handleMarginTabClick = () => {
    const assetTabLabel = 'bn.selectedAssetTab';
    setItem(window.localStorage, assetTabLabel, 'margin');
    this.props.setSelectedAssetTab('margin');
  }

_showTip = ({ index, rowData }: Object) => {
  this.setState({
    isShowTip: true,
    onHoverSymbol: { index, data: rowData[1] },
  });
}

_hideTip = () => {
  this.setState({
    isShowTip: false,
  });
}

_toggleStableCurrencies = (coin: string) => () => {
  const { stableCurrenciesMenuStatus } = this.state;
  if (!stableCurrenciesMenuStatus) return null;
  const newStableCurrenciesMenuStatus = { ...stableCurrenciesMenuStatus };
  newStableCurrenciesMenuStatus[coin] = !stableCurrenciesMenuStatus[coin];
  this.setState({ stableCurrenciesMenuStatus: newStableCurrenciesMenuStatus });
  return null;
}

_getScrollTop = (scrollTop: number) => {
  this.tableScrollTop = scrollTop;
}

_renderRowClassName = ({ index }: Object) => {
  const { symbol } = this.props;
  if (index !== -1) {
    return this._tickers[index][0] === symbol ? 'active' : '';
  }
  return null;
}

_handleTabClick = (filterByAsset: string) => {
  const { stableCoins }: { stableCoins: mixed } = this._getParentMarketNamesAndStableCoins(this.props);
  const { selectedStableCoinTabs } = this.props;
  this.setState({
    filterByAsset,
    showFavorites: false,
    scrollHeight: this._tickers.length * 18,
  });
  const assetTabLabel = 'bn.selectedAssetTab';
  const stableCoinTabsLabel = 'bn.selectedStableCoinTabs';

  // Returs an array and its leading value would be the parent coin. ex: ['USDⓈ', 'USDT']
  const stableCoinsPerParent = Object.values(stableCoins).find(
    (el: mixed) => Array.isArray(el) && el.includes(filterByAsset)
  );

  if (stableCoins && stableCoinsPerParent && Array.isArray(stableCoinsPerParent)) {
    // first coin will always be the main index for each dropdown
    const indexCoin = stableCoinsPerParent[0];
    const newSelectedStableCoinTabs = { ...selectedStableCoinTabs };
    newSelectedStableCoinTabs[indexCoin] = filterByAsset;
    setItem(window.localStorage, stableCoinTabsLabel, JSON.stringify(newSelectedStableCoinTabs));
    setItem(window.localStorage, assetTabLabel, indexCoin);
    this.props.setSelectedAssetTab(indexCoin);
    this.props.setSelectedStableCoinTabs(newSelectedStableCoinTabs);
  } else {
    setItem(window.localStorage, assetTabLabel, filterByAsset);
    this.props.setSelectedAssetTab(filterByAsset);
  }
}

_getStableCurrencyCollectionLabel = (coin: string, stableCoins: Array<string>) => {
  const { selectedStableCoinTabs } = this.props;
  const { filterByAsset } = this.state;
  if (!stableCoins.includes(filterByAsset)) return selectedStableCoinTabs[coin];
  const index = stableCoins.indexOf(filterByAsset);
  return index > -1 ? stableCoins[index] : coin;
}

_isMarginPair = (current: string, sourceArr: Array<Object>) => {
  if (sourceArr.filter(s => s.symbol === current).length >= 1) return true;
  return false;
}

_headerRenderer({
  label,
  dataKey,
  sortBy,
  sortDirection,
}: HeaderRenderer) {
  return (
    <HeaderCell dataKey={dataKey}>
      {label}
      {sortBy === dataKey && <SortIndicator sortDirection={sortDirection} />}
    </HeaderCell>
  );
}

_getFavorites() {
  return this.props.favorites;
}

_getParentMarketNamesAndStableCoins(props: Props): Object {
  const { tickers } = props;
  const parentMarketNames = [];
  const stableCoins = {};
  STABLE_COIN_BASES.forEach((el: string) => {
    stableCoins[el] = [el];
  });
  const list = Object.values(tickers);
  list.forEach(({ pn, qa }: any) => {
    if (pn && !STABLE_COIN_BASES.includes(pn) && !parentMarketNames.includes(pn)) {
      parentMarketNames.push(pn);
    }
    STABLE_COIN_BASES.forEach((el: string) => {
      if (el === pn && STABLE_COIN_BASES.includes(pn) && !stableCoins[el].includes(qa)) {
        stableCoins[el].push(qa);
      }
    });
  });
  return { parentMarketNames, stableCoins };
}


render() {
  const {
    i18n,
    locale,
    selectedAssetTab,
    selectedStableCoinTabs,
    omitTabs,
    omitFavorites
  } = this.props;
  const {
    showFavorites,
    showMarginTooltip,
    sortBy,
    sortDirection,
    togglePanel,
    priceColor,
    isShowTip,
    onHoverSymbol,
    isScrolling,
    stableCurrenciesMenuStatus,
  } = this.state;
  const tickers = this._tickers = this._getTickers(this.state);
  const _sort = ({ sortBy: passedSortBy, sortDirection: passedSortDirection }) => {
    this.setState({
      sortBy: passedSortBy,
      sortDirection: passedSortDirection,
    });
  };
  const { parentMarketNames, stableCoins } = this._getParentMarketNamesAndStableCoins(this.props);
  return (
    <FlexBox bg="theme" flex="1" direction="column" wrap="nowrap">
      {omitTabs ? null : (
        <Tabs>
          <LeftPanel flex="1" wrap="nowrap">
            {omitFavorites ? null : (
              <FavoritesTab
                selected={selectedAssetTab === 'favorites'}
                onClick={this._handleFavoriteTabClick}
                margin="0"
              >
                <i className={`iconfont icon-collect-fill ${selectedAssetTab === 'favorites' ? 'active' : ''}`} />
              </FavoritesTab>
            )}
            {enableMargin?
            <MarginTab
              selected={selectedAssetTab === 'margin'}
              onClick={this._handleMarginTabClick}
              onMouseEnter={() =>this.setState({showMarginTooltip:true})}
              onMouseLeave={() => this.setState({ showMarginTooltip: false })}
              margin="0"
            >
              <MarginIcon noborder lg unselected={selectedAssetTab !== 'margin'}/>
              {showMarginTooltip?<Tooltip>{getLabel('exchange-trade-marginSymbolTooltip', i18n)}</Tooltip>:null}
            </MarginTab>
            :null}
          </LeftPanel>
          <RightPanel>
            {parentMarketNames.map((v) => (
              <TabColor
                key={v}
                selected={selectedAssetTab === v && !showFavorites}
                onClick={() => this._handleTabClick(v)}
              >
                {v}
              </TabColor>
            ))}
            {stableCurrenciesMenuStatus && STABLE_COIN_BASES.map(
              // Greater than 1 here. The first one is the index coin,
              // don't show dropdown if length equals to 1
              (coin: string) => stableCoins[coin].length > 1 ? (
                <StableCurrencyContainer key={coin} wrap="nowrap">
                  <TabColor
                    selected={stableCoins[coin].includes(
                      selectedStableCoinTabs[coin]
                    ) && !showFavorites && selectedAssetTab === coin}
                    margin="0 0 0 2px"
                    className="stable-currency-collection"
                    onClick={() => {
                      this._handleTabClick(
                        this._getStableCurrencyCollectionLabel(coin, stableCoins[coin])
                      );
                    }}
                  >
                    {this._getStableCurrencyCollectionLabel(coin, stableCoins[coin]) || coin}
                  </TabColor>
                  <TabColor
                    onClick={this._toggleStableCurrencies(coin)}
                    margin="0"
                    className="stable-currency-cta"
                  >
                    <MenuIcon toggled={stableCurrenciesMenuStatus[coin]}>▾</MenuIcon>
                  </TabColor>
                  <StableCurrencyDropdown
                    hide={!stableCurrenciesMenuStatus[coin]}
                    i18n={i18n}
                    keys={stableCoins[coin]}
                    onClickHandler={this._handleTabClick}
                    onMouseLeave={this._toggleStableCurrencies(coin)}
                    selectedTab={selectedAssetTab}
                  />
                </StableCurrencyContainer>
              ) : null)}
          </RightPanel>
        </Tabs>
      )}
      <PaddedFlexBox>
        <Form>
          <FlexBox wrap="nowrap">
            <FlexBox>
              <SearchInput
                value={this.state.keyword}
                placeholder={`${getLabel('exchange-trade-search', i18n)} …`}
                onChange={this._onSearchBarChange}
                width="100px"
              />
            </FlexBox>
            <RadiosPanel>
              <RadioPanelLabel htmlFor="field-change">
                <RadioPanelInput
                  type="radio"
                  name="togglePanel"
                  id="field-change"
                  value="change"
                  checked={togglePanel === 'change' ? 'checked' : ''}
                  onChange={this._onTogglePanelChange}
                />
                <RadioPanelIcon />
                {getLabel('exchange-trade-fieldChange', i18n)}
              </RadioPanelLabel>
              <RadioPanelLabel htmlFor="field-volume">
                <RadioPanelInput
                  type="radio"
                  name="togglePanel"
                  id="field-volume"
                  value="volume"
                  checked={togglePanel === 'volume' ? 'checked' : ''}
                  onChange={this._onTogglePanelChange}
                />
                <RadioPanelIcon />
                {getLabel('exchange-trade-fieldVolume', i18n)}
              </RadioPanelLabel>
            </RadiosPanel>
          </FlexBox>
        </Form>
      </PaddedFlexBox>
      <TableFlexBox flex="1 0 auto" onMouseLeave={this._hideTip}>
        {isShowTip ? (
          <FiatPriceBox
            index={onHoverSymbol.index}
            rowHeight={ROW_HEIGHT}
            scrollTop={this.tableScrollTop}
          >
            <FiatPrice
              price={onHoverSymbol.data.c}
              quoteAsset={onHoverSymbol.data.qa}
              precision={2}
              color="white"
            />
            <a
              href={prefixLink(
                `/${locale}/trade/${onHoverSymbol.data.ba}_${onHoverSymbol.data.qa}`
              )}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Icon type="newWindow" size={12} color="#fff" />
            </a>
          </FiatPriceBox>) : null}
        <ScrollableTable
          defaultWidth={DEFAULT_WIDTH}
          defaultHeight={DEFAULT_HEIGHT}
          headerHeight={HEADER_HEIGHT}
          rowHeight={ROW_HEIGHT}
          rowCount={tickers.length}
          rowGetter={this._rowGetter}
          rowRenderer={this._rowRenderer}
          onRowClick={this._onRowClick}
          onRowsRendered={this._onRowsRendered}
          rowStyle={ROW_STYLE}
          sort={_sort}
          sortBy={sortBy}
          sortDirection={sortDirection}
          noRowsRenderer={this._noRowsRenderer}
          nonce={typeof window !== 'undefined' && window.__NONCE__}
          onRowMouseOver={this._showTip}
          getScrollTop={this._getScrollTop}
          rowClassName={this._renderRowClassName}
          isHeaderBgWhite={true}
        >
          {!omitFavorites && this._getFavoritesColumn(!!isScrolling)}
          <ScrollableTable.Column
            label={getLabel('exchange-trade-Pair', i18n)}
            dataKey="pair"
            width={90}
            cellDataGetter={this._cellGetterPair}
            cellRenderer={this._cellRendererPair}
            headerRenderer={this._headerRenderer}
            style={PAIR_COL_STYLE}
          />
          <ScrollableTable.Column
            label={getLabel('exchange-trade-Price', i18n)}
            dataKey="price"
            width={75}
            cellDataGetter={this._cellGetter}
            cellRenderer={(cellData): CellDataGetter => (
              this._cellRendererPrice(cellData.cellData, priceColor)
            )}
            headerRenderer={this._headerRenderer}
            style={PRICE_COL_STYLE}
          />
          <ScrollableTable.Column
            label={togglePanel === 'change' ? getLabel('exchange-trade-fieldChange', i18n) : getLabel('exchange-trade-fieldVolume', i18n)}
            dataKey={togglePanel === 'change' ? 'change' : 'volume'}
            width={75}
            cellDataGetter={togglePanel === 'change' ? this._cellGetterChange : this._cellGetterVolume}
            cellRenderer={togglePanel === 'change' ? this._cellRendererChange : this._cellRendererVolume}
            headerRenderer={this._headerRenderer}
            headerStyle={ALIGN_RIGHT}
            style={ALIGN_RIGHT}
          />
        </ScrollableTable>
      </TableFlexBox>
    </FlexBox>
  );
}
}
